/**
 * ConsentPreferenceManager — Privacy Centre
 * Route: /privacy-centre
 *
 * A dedicated page where users can:
 *  a) View current consent status
 *  b) Manage preferences (toggle purposes on/off)
 *  c) Exercise their data rights (access, erasure, correction, grievance)
 *  d) Track request status
 *  e) View DPO contact details
 *  f) View full consent history
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  CheckCircle,
  XCircle,
  Download,
  Trash2,
  AlertTriangle,
  Clock,
  ChevronRight,
  History,
  Settings,
  FileText,
  MessageSquare,
  ArrowLeft,
  RefreshCw,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useConsent, CONSENT_PURPOSES, getPurposeLabel } from "@/hooks/useConsent";
import {
  fileRightsRequest,
  getRightsRequest,
  getConsentStatus,
  getConsentHistory,
  getParentChildren,
  revokeChildConsent,
} from "@/services/dpdpApi";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Correction form schema
// ---------------------------------------------------------------------------

const correctionSchema = z.object({
  field_name: z.string().min(1, "Field name is required"),
  current_value: z.string().min(1, "Current value is required"),
  corrected_value: z.string().min(1, "Corrected value is required"),
  reason: z.string().min(10, "Please provide a reason (at least 10 characters)"),
});

const grievanceSchema = z.object({
  subject: z.string().min(5, "Subject must be at least 5 characters"),
  description: z.string().min(20, "Please describe your grievance (at least 20 characters)"),
  preferred_response: z.enum(["email", "phone", "whatsapp"]).optional(),
});

// ---------------------------------------------------------------------------
// ConsentPreferenceManager
// ---------------------------------------------------------------------------

export default function ConsentPreferenceManager() {
  const navigate = useNavigate();
  const {
    consentStatus,
    consentHistory,
    loading,
    granting,
    withdrawing,
    loadNotice,
    activeNotice,
    syncWithBackend,
    withdrawPurposes,
    acceptPurposes,
  } = useConsent();

  const [activeTab, setActiveTab] = useState("status");
  const [showErasureWarning, setShowErasureWarning] = useState(false);
  const [requestIdInput, setRequestIdInput] = useState("");
  const [trackedRequest, setTrackedRequest] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);

  // Parental consent — children linked to this parent account
  const [childrenList, setChildrenList] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  // Correction form
  const correctionForm = useForm({
    resolver: zodResolver(correctionSchema),
    defaultValues: { field_name: "", current_value: "", corrected_value: "", reason: "" },
  });

  // Grievance form
  const grievanceForm = useForm({
    resolver: zodResolver(grievanceSchema),
    defaultValues: { subject: "", description: "", preferred_response: "email" },
  });

  // Local toggle state (synced from consentStatus)
  const [localGranted, setLocalGranted] = useState(new Set());

  useEffect(() => {
    if (consentStatus?.purposes) {
      const granted = new Set(
        consentStatus.purposes
          .filter((p) => p.status === "granted")
          .map((p) => p.purpose_code)
      );
      setLocalGranted(granted);
    }
  }, [consentStatus]);

  // Load children list when the children tab is activated
  useEffect(() => {
    if (activeTab !== "children") return;
    setChildrenLoading(true);
    getParentChildren()
      .then((res) => setChildrenList(res.children || []))
      .catch(() => setChildrenList([]))
      .finally(() => setChildrenLoading(false));
  }, [activeTab]);

  const handleToggle = async (purposeCode) => {
    const isCurrentlyGranted = localGranted.has(purposeCode);
    const purpose = CONSENT_PURPOSES.find((p) => p.code === purposeCode);

    if (!purpose) return;
    if (!purpose.canWithdraw && isCurrentlyGranted) {
      toast.error("This purpose cannot be withdrawn — it is required for platform operation.");
      return;
    }

    try {
      if (isCurrentlyGranted) {
        // Withdraw
        setLocalGranted((prev) => {
          const next = new Set(prev);
          next.delete(purposeCode);
          return next;
        });
        await withdrawPurposes([purposeCode]);
        toast.success(`${purpose.label} consent withdrawn`);
      } else {
        // Grant
        setLocalGranted((prev) => new Set([...prev, purposeCode]));
        await acceptPurposes([purposeCode], activeNotice?.notice_id);
        toast.success(`${purpose.label} consent granted`);
      }
    } catch (err) {
      // Revert on error
      if (isCurrentlyGranted) {
        setLocalGranted((prev) => new Set([...prev, purposeCode]));
      } else {
        setLocalGranted((prev) => {
          const next = new Set(prev);
          next.delete(purposeCode);
          return next;
        });
      }
      toast.error(`Failed to update consent: ${err.message}`);
    }
  };

  const handleFileRightsRequest = async (requestType, details = {}) => {
    try {
      const result = await fileRightsRequest({
        request_type: requestType,
        details,
      });
      toast.success(`Request filed! Your request ID is: ${result.request_id}`);
      setPendingRequests((prev) => [result, ...prev]);
      setActiveTab("track");
      return result;
    } catch (err) {
      toast.error(`Failed to file request: ${err.message}`);
      throw err;
    }
  };

  const handleErasureRequest = async () => {
    setShowErasureWarning(false);
    return handleFileRightsRequest("erasure", {
      warning_acknowledged: true,
    });
  };

  const handleCorrectionSubmit = async (data) => {
    await handleFileRightsRequest("correction", {
      field_name: data.field_name,
      current_value: data.field_name,
      corrected_value: data.corrected_value,
      reason: data.reason,
    });
    correctionForm.reset();
  };

  const handleGrievanceSubmit = async (data) => {
    await handleFileRightsRequest("grievance", {
      subject: data.subject,
      description: data.description,
      preferred_response: data.preferred_response,
    });
    grievanceForm.reset();
  };

  const handleTrackRequest = async () => {
    if (!requestIdInput.trim()) {
      toast.error("Please enter a request ID");
      return;
    }
    setTrackingLoading(true);
    try {
      const result = await getRightsRequest(requestIdInput.trim());
      setTrackedRequest(result);
    } catch (err) {
      setTrackedRequest(null);
      toast.error(`Request not found: ${err.message}`);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleRevokeChild = async (verificationId) => {
    if (
      !confirm(
        "Are you sure you want to revoke consent for this child's account? They will lose access to the platform."
      )
    ) {
      return;
    }
    setRevokingId(verificationId);
    try {
      await revokeChildConsent(verificationId);
      setChildrenList((prev) =>
        prev.filter((c) => c.verification_id !== verificationId)
      );
      toast.success("Consent revoked successfully.");
    } catch (err) {
      toast.error(`Failed to revoke: ${err.message}`);
    } finally {
      setRevokingId(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      granted: <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Granted</Badge>,
      withdrawn: <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Withdrawn</Badge>,
      never_given: <Badge variant="outline">Never Given</Badge>,
      received: <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Received</Badge>,
      in_progress: <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">In Progress</Badge>,
      completed: <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Completed</Badge>,
      rejected: <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Rejected</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                <Shield className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
              </div>
              <div>
                <h1 className="text-lg font-semibold">Privacy Centre</h1>
                <p className="text-xs text-muted-foreground">
                  Manage your data rights under DPDP Act 2023
                </p>
              </div>
            </div>
            <div className="ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={syncWithBackend}
                className="gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 lg:grid-cols-7 w-full h-auto bg-muted/50">
            <TabsTrigger value="status" className="text-xs sm:text-sm gap-1">
              <CheckCircle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Status</span>
            </TabsTrigger>
            <TabsTrigger value="manage" className="text-xs sm:text-sm gap-1">
              <Settings className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Manage</span>
            </TabsTrigger>
            <TabsTrigger value="rights" className="text-xs sm:text-sm gap-1">
              <FileText className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Rights</span>
            </TabsTrigger>
            <TabsTrigger value="track" className="text-xs sm:text-sm gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Track</span>
            </TabsTrigger>
            <TabsTrigger value="children" className="text-xs sm:text-sm gap-1">
              <Users className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">Children</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="text-xs sm:text-sm gap-1">
              <History className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">History</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="text-xs sm:text-sm gap-1">
              <MessageSquare className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">DPO</span>
            </TabsTrigger>
          </TabsList>

          {/* ── (a) Current Consent Status ── */}
          <TabsContent value="status" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Current Consent Status
                </CardTitle>
                <CardDescription>
                  Your consent records as of{" "}
                  {consentStatus?.checked_at
                    ? format(new Date(consentStatus.checked_at), "PPpp")
                    : "loading..."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Purpose</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Granted At</TableHead>
                        <TableHead>Notice Version</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {CONSENT_PURPOSES.map((purpose) => {
                        const record = consentStatus?.purposes?.find(
                          (p) => p.purpose_code === purpose.code
                        );
                        const status = record?.status || "never_given";
                        return (
                          <TableRow key={purpose.code}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{purpose.label}</p>
                                <p className="text-xs text-muted-foreground">
                                  {purpose.description}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {statusBadge(status)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {record?.granted_at
                                ? format(new Date(record.granted_at), "PP")
                                : "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {record?.notice_version || "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── (b) Manage Preferences ── */}
          <TabsContent value="manage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Manage Preferences
                </CardTitle>
                <CardDescription>
                  Toggle consent for each purpose. Essential purposes cannot be turned off.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {CONSENT_PURPOSES.map((purpose) => {
                  const isGranted = localGranted.has(purpose.code);
                  return (
                    <div
                      key={purpose.code}
                      className="flex items-center justify-between py-3 border-b last:border-0"
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="text-sm font-medium">{purpose.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {purpose.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {!purpose.canWithdraw && isGranted && (
                          <Badge variant="outline" className="text-xs">
                            Required
                          </Badge>
                        )}
                        <Switch
                          checked={isGranted}
                          onCheckedChange={() => handleToggle(purpose.code)}
                          disabled={
                            (purpose.required && isGranted) ||
                            granting ||
                            withdrawing
                          }
                          aria-label={purpose.label}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── (c) Exercise Your Rights ── */}
          <TabsContent value="rights" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Download my data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Download className="h-4 w-4" />
                    Download My Data
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Get a copy of all your personal data in machine-readable format
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">
                    Your data will be compiled and made available within 30 days
                    (DPDP Section 12 — Right to Portability).
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      handleFileRightsRequest("access", {
                        format: "json",
                      })
                    }
                  >
                    Request Data Export
                  </Button>
                </CardContent>
              </Card>

              {/* Delete my data */}
              <Card className="border-red-200 dark:border-red-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base text-red-600 dark:text-red-400">
                    <Trash2 className="h-4 w-4" />
                    Delete My Data
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Erase all your personal data ("right to be forgotten")
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-red-600 dark:text-red-400 mb-3 font-medium">
                    Warning: This action is irreversible. Your account, psychometric
                    data, applications, and all personal data will be permanently deleted.
                  </p>
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => setShowErasureWarning(true)}
                  >
                    Request Data Erasure
                  </Button>
                </CardContent>
              </Card>

              {/* Correct my data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4" />
                    Correct My Data
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Update inaccurate or incomplete personal data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={correctionForm.handleSubmit(handleCorrectionSubmit)}
                    className="space-y-3"
                  >
                    <div>
                      <Label className="text-xs">Field name</Label>
                      <Input
                        {...correctionForm.register("field_name")}
                        placeholder="e.g. phone number"
                        className="h-9 text-sm mt-1"
                      />
                      {correctionForm.formState.errors.field_name && (
                        <p className="text-xs text-red-500 mt-1">
                          {correctionForm.formState.errors.field_name.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Current value</Label>
                      <Input
                        {...correctionForm.register("current_value")}
                        placeholder="What is the current (incorrect) value?"
                        className="h-9 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Corrected value</Label>
                      <Input
                        {...correctionForm.register("corrected_value")}
                        placeholder="What should it be?"
                        className="h-9 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reason</Label>
                      <Textarea
                        {...correctionForm.register("reason")}
                        placeholder="Please explain why this needs to be corrected..."
                        className="text-sm mt-1"
                        rows={2}
                      />
                    </div>
                    <Button type="submit" variant="outline" size="sm" className="w-full">
                      Submit Correction Request
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Raise a grievance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MessageSquare className="h-4 w-4" />
                    Raise a Grievance
                  </CardTitle>
                  <CardDescription className="text-sm">
                    File a complaint about data processing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={grievanceForm.handleSubmit(handleGrievanceSubmit)}
                    className="space-y-3"
                  >
                    <div>
                      <Label className="text-xs">Subject</Label>
                      <Input
                        {...grievanceForm.register("subject")}
                        placeholder="Brief subject of your grievance"
                        className="h-9 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        {...grievanceForm.register("description")}
                        placeholder="Describe your grievance in detail..."
                        className="text-sm mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Preferred response</Label>
                      <div className="flex gap-2 mt-1">
                        {["email", "phone", "whatsapp"].map((method) => (
                          <Button
                            key={method}
                            type="button"
                            variant={
                              grievanceForm.watch("preferred_response") === method
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className="flex-1 text-xs h-8"
                            onClick={() =>
                              grievanceForm.setValue("preferred_response", method)
                            }
                          >
                            {method.charAt(0).toUpperCase() + method.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" variant="outline" size="sm" className="w-full">
                      Submit Grievance
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── (d) Track Request Status ── */}
          <TabsContent value="track" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Track a Request
                </CardTitle>
                <CardDescription>
                  Enter a request ID to check its status
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={requestIdInput}
                    onChange={(e) => setRequestIdInput(e.target.value)}
                    placeholder="Enter request ID (e.g. uuid)"
                    className="text-sm"
                    onKeyDown={(e) => e.key === "Enter" && handleTrackRequest()}
                  />
                  <Button onClick={handleTrackRequest} disabled={trackingLoading}>
                    {trackingLoading ? "Tracking..." : "Track"}
                  </Button>
                </div>

                {trackedRequest && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">
                        Request: {trackedRequest.request_id}
                      </h4>
                      {statusBadge(trackedRequest.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Type:</span>{" "}
                        {trackedRequest.request_type}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Filed:</span>{" "}
                        {format(new Date(trackedRequest.requested_at), "PP")}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Due by:</span>{" "}
                        {format(new Date(trackedRequest.due_by), "PP")}
                      </div>
                      <div>
                        <span className="text-muted-foreground">SLA Breached:</span>{" "}
                        {trackedRequest.sla_breached ? (
                          <XCircle className="h-3.5 w-3.5 text-red-500 inline" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 inline" />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {pendingRequests.length > 0 && (
                  <>
                    <Separator />
                    <h4 className="text-sm font-medium">Recent Requests</h4>
                    {pendingRequests.map((req) => (
                      <div
                        key={req.request_id}
                        className="rounded-lg border p-3 flex items-center justify-between"
                      >
                        <div className="text-xs">
                          <p className="font-medium">{req.request_type}</p>
                          <p className="text-muted-foreground">{req.request_id}</p>
                        </div>
                        {statusBadge(req.status)}
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── (d-2) Children — Parental Consent Dashboard ── */}
          <TabsContent value="children" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Children&apos;s Accounts
                </CardTitle>
                <CardDescription>
                  Accounts where you have verified parental consent as a guardian
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {childrenLoading ? (
                  <div className="space-y-3">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                ) : childrenList.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No children&apos;s accounts linked to your account.
                    <p className="mt-2 text-xs">
                      If you received a parental consent request, use the link in
                      your email to verify.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {childrenList.map((child) => (
                      <div
                        key={child.verification_id}
                        className="flex items-center justify-between rounded-lg border p-4"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                            <Users className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Minor Account</p>
                            <p className="text-xs text-muted-foreground">
                              Verified:{" "}
                              {child.granted_at
                                ? format(new Date(child.granted_at), "PP")
                                : "N/A"}{" "}
                              &middot;{" "}
                              {child.parent_verification_method === "digilocker"
                                ? "Via DigiLocker"
                                : "Via Account"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {statusBadge(child.consent_status)}
                          {child.consent_status === "verified" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() =>
                                handleRevokeChild(child.verification_id)
                              }
                              disabled={revokingId === child.verification_id}
                            >
                              {revokingId === child.verification_id
                                ? "Revoking..."
                                : "Revoke Consent"}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── (e) DPO Contact ── */}
          <TabsContent value="contact" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Data Protection Officer
                </CardTitle>
                <CardDescription>
                  Contact our DPO for data protection matters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 shrink-0">
                      <Shield className="h-6 w-6 text-indigo-600 dark:text-indigo-300" />
                    </div>
                    <div>
                      <p className="font-semibold">Data Protection Officer</p>
                      <p className="text-sm text-muted-foreground">
                        Right Doers World Pvt. Ltd.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Email:</span>
                      <a
                        href="mailto:dpo@rightdoersworld.com"
                        className="text-indigo-600 dark:text-indigo-300 underline"
                      >
                        dpo@rightdoersworld.com
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Response SLA:</span>
                      <Badge>30 days (DPDP Section 11)</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Your Rights Under DPDP 2023</h4>
                  <Accordion type="single" collapsible className="w-full">
                    {[
                      {
                        title: "Right to Access (Section 11)",
                        desc: "You can request access to a summary of personal data processed, the nature of personal data processed, and the purpose of processing.",
                      },
                      {
                        title: "Right to Erasure (Section 11)",
                        desc: "You can request deletion of your personal data when it is no longer necessary for the purpose it was collected, or when you withdraw consent.",
                      },
                      {
                        title: "Right to Portability (Section 12)",
                        desc: "You can receive your personal data in a structured, commonly used, and machine-readable format.",
                      },
                      {
                        title: "Right to Correction (Section 11)",
                        desc: "You can request correction of inaccurate, incomplete, or out-of-date personal data.",
                      },
                      {
                        title: "Right to Grievance (Section 13)",
                        desc: "You have the right to raise a complaint with the DPO. If unsatisfied with the DPO's response within 30 days, you may approach the Data Protection Board of India (DPB).",
                      },
                    ].map((item) => (
                      <AccordionItem key={item.title} value={item.title}>
                        <AccordionTrigger className="text-sm">{item.title}</AccordionTrigger>
                        <AccordionContent className="text-xs text-muted-foreground">
                          {item.desc}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── (f) Consent History ── */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Consent Event History
                </CardTitle>
                <CardDescription>
                  Chronological log of all consent events on your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                {consentHistory.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No consent events recorded yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {consentHistory.map((event, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                          {event.event_type === "granted" ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">
                              {getPurposeLabel(event.purpose_code) || event.purpose_code}
                            </p>
                            <Badge variant="outline" className="text-xs capitalize">
                              {event.event_type}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Notice v{event.notice_version} ({event.notice_language}) &middot;{" "}
                            {event.channel} &middot;{" "}
                            {format(new Date(event.created_at), "PPpp")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Erasure warning dialog */}
      <AlertDialog open={showErasureWarning} onOpenChange={setShowErasureWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Permanent Data Erasure
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                You are about to request the permanent deletion of all your personal
                data. This action is <strong>irreversible</strong>.
              </p>
              <p>The following will be deleted:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your account and profile</li>
                <li>Psychometric assessment results (Big 5 scores)</li>
                <li>Job applications and related data</li>
                <li>D-COIN wallet and transaction history</li>
                <li>Family connections</li>
                <li>All other personal data in our system</li>
              </ul>
              <p>
                Legal documents (NDA/offer letters signed via WhatsApp) will be
                retained as required by law for 7 years per DPDP Section 8(1).
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowErasureWarning(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleErasureRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              I understand — delete my data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
